'use client';

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { authService } from '@/lib/authService';
import { createLogger } from '@/lib/debugLogger';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

const logger = createLogger('useCustomerJobSearch');

export interface Customer {
  id: number;
  company_name: string | null;
  contact_person: string | null;
  email: string | null;
}

export interface Job {
  id: string;
  item_number: string;
  title: string;
  status: string;
  contact_name?: string | null;
}

interface UseCustomerJobSearchParams {
  isJobLinkedExpense: boolean;
}

export function useCustomerJobSearch({ isJobLinkedExpense }: UseCustomerJobSearchParams) {
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<{ id: number; company_name: string | null; contact_person: string | null } | null>(null);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  const [activeJobs, setActiveJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<{ id: string; item_number: string; title: string; contact_name?: string | null } | null>(null);
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [showJobSearch, setShowJobSearch] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobsChecked, setJobsChecked] = useState(false);

  const customerJobsCacheRef = useRef(new Map<number, Job[]>());
  const latestJobLoadRequestId = useRef(0);
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const jobDropdownRef = useRef<HTMLDivElement>(null);

  const loadCustomers = useCallback(async () => {
    if (allCustomers.length > 0) return;
    setLoadingCustomers(true);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(
        '/api/v1/contacts?contact_types=customer&is_active=true&limit=500',
        { headers: { Authorization: `Bearer ${token || ''}` } }
      );
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setAllCustomers(
          data.data.map((c: { id?: number; company_name?: string | null; contact_person?: string | null; email?: string | null }) => ({
            id: Number(c.id) || 0,
            company_name: c.company_name || null,
            contact_person: c.contact_person || null,
            email: c.email || null,
          }))
        );
      }
    } catch (error) {
      if (!isSilentFetchError(error)) logger.warn('[useCustomerJobSearch] Failed to load customers:', error);
    } finally {
      setLoadingCustomers(false);
    }
  }, [allCustomers.length]);

  // Auto-load customers when job-linked expense is selected
  useEffect(() => {
    if (isJobLinkedExpense && allCustomers.length === 0) {
      void loadCustomers();
    }
  }, [isJobLinkedExpense, allCustomers.length, loadCustomers]);

  const filteredCustomers = useMemo(() => {
    if (!customerSearchQuery.trim()) return allCustomers;
    const q = customerSearchQuery.toLowerCase();
    return allCustomers.filter(
      (c) =>
        (c.company_name && c.company_name.toLowerCase().includes(q)) ||
        (c.contact_person && c.contact_person.toLowerCase().includes(q)) ||
        (c.email && c.email.toLowerCase().includes(q))
    );
  }, [allCustomers, customerSearchQuery]);

  const loadActiveJobs = useCallback(async (contactId: number, options: { forceRefresh?: boolean } = {}) => {
    const requestId = ++latestJobLoadRequestId.current;

    if (!options.forceRefresh && customerJobsCacheRef.current.has(contactId)) {
      const cachedJobs = customerJobsCacheRef.current.get(contactId) || [];
      setActiveJobs(cachedJobs);
      setLoadingJobs(false);
      setJobsChecked(true);
      return;
    }

    setLoadingJobs(true);
    setJobsChecked(false);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(
        `/api/v1/work-items?item_type=job&contact_id=${contactId}&limit=200`,
        { headers: { Authorization: `Bearer ${token || ''}` } }
      );
      if (requestId !== latestJobLoadRequestId.current) return;
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        const jobs = data.data as Job[];
        const filtered = jobs.filter(
          (j) => !['completed', 'cancelled', 'closed', 'resolved'].includes(j.status)
        );
        customerJobsCacheRef.current.set(contactId, filtered);
        setActiveJobs(filtered);
      } else {
        setActiveJobs([]);
      }
    } catch (error) {
      if (!isSilentFetchError(error)) logger.warn('[useCustomerJobSearch] Failed to load active jobs:', error);
      setActiveJobs([]);
    } finally {
      if (requestId === latestJobLoadRequestId.current) {
        setLoadingJobs(false);
        setJobsChecked(true);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      void loadActiveJobs(selectedCustomer.id);
    } else {
      setActiveJobs([]);
      setSelectedJob(null);
      setJobSearchQuery('');
      setJobsChecked(false);
    }
  }, [selectedCustomer, loadActiveJobs]);

  const filteredJobs = useMemo(() => {
    if (!jobSearchQuery.trim()) return activeJobs;
    const q = jobSearchQuery.toLowerCase();
    return activeJobs.filter(
      (j) =>
        j.item_number.toLowerCase().includes(q) ||
        j.title.toLowerCase().includes(q) ||
        (j.contact_name?.toLowerCase().includes(q) || false)
    );
  }, [activeJobs, jobSearchQuery]);

  // Close dropdowns on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setShowCustomerSearch(false);
      }
      if (jobDropdownRef.current && !jobDropdownRef.current.contains(event.target as Node)) {
        setShowJobSearch(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const reset = useCallback(() => {
    setSelectedCustomer(null);
    setCustomerSearchQuery('');
    setShowCustomerSearch(false);
    setSelectedJob(null);
    setJobSearchQuery('');
    setShowJobSearch(false);
    setActiveJobs([]);
    setJobsChecked(false);
    setAllCustomers([]);
    setLoadingCustomers(false);
    setLoadingJobs(false);
    customerJobsCacheRef.current.clear();
  }, []);

  return {
    allCustomers,
    selectedCustomer,
    setSelectedCustomer,
    customerSearchQuery,
    setCustomerSearchQuery,
    showCustomerSearch,
    setShowCustomerSearch,
    loadingCustomers,
    filteredCustomers,
    activeJobs,
    selectedJob,
    setSelectedJob,
    jobSearchQuery,
    setJobSearchQuery,
    showJobSearch,
    setShowJobSearch,
    loadingJobs,
    jobsChecked,
    filteredJobs,
    customerDropdownRef,
    jobDropdownRef,
    reset,
  };
}
